import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const WithdrawalHistory = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // ✅ Removed unused 'totalRecords' state

  const UserId = localStorage.getItem("UserId");

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    let label = "Unknown";
    let color = "#6b7280";

    const statusStr = String(status).toLowerCase();

    if (statusStr === "0" || statusStr === "pending") {
      label = "Pending";
      color = "#f59e0b";
    } else if (statusStr === "1" || statusStr === "success" || statusStr === "completed") {
      label = "Completed";
      color = "#10b981";
    } else if (statusStr === "2" || statusStr === "failed") {
      label = "Failed";
      color = "#ef4444";
    }

    return (
      <span className="status-badge" style={{ backgroundColor: color }}>
        {label}
      </span>
    );
  };

  // Fetch withdrawal history
  const fetchWithdrawals = useCallback(async () => {
    if (!UserId) {
      setWithdrawals([]);
      setError("User not logged in");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const res = await axios.post(`${API_BASE}/withdrawalhistory2`, { UserId });
      
      console.log("Withdrawal API Response:", res.data);
      
      if (res.data.success) {
        if (res.data.data && res.data.data.length > 0) {
          const formattedData = res.data.data.map((record, index) => ({
            id: record.id || `WD${String(index + 1).padStart(4, '0')}`,
            amount: record.amount || 0,
            netamount: record.netamount || 0,
            method: 'USDT (BEP20)',
            address: record.address || 'N/A',
            date: record.created_at ? new Date(record.created_at).toLocaleString() : '-',
            status: record.txstatus,
            txHash: record.txhash || '-',
            remarks: record.remarks || '-',
            tokenAmount: record.tokenAmount || 0
          }));
          setWithdrawals(formattedData);
          setError("");
        } else {
          setWithdrawals([]);
          setError("No withdrawal records found");
        }
      } else {
        setWithdrawals([]);
        setError(res.data.error || "No withdrawal records found");
      }
    } catch (err) {
      console.error("Withdrawal History Error:", err);
      setError("Failed to fetch withdrawal history");
      setWithdrawals([]);
      toast.error("Failed to load withdrawal history");
    } finally {
      setLoading(false);
    }
  }, [UserId]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  // Filter withdrawals - Convert id to string safely
  const filteredWithdrawals = withdrawals.filter(item => {
    let matchesFilter = true;
    const statusStr = String(item.status).toLowerCase();
    
    if (filter === 'completed') {
      matchesFilter = statusStr === "1" || statusStr === "success" || statusStr === "completed";
    } else if (filter === 'pending') {
      matchesFilter = statusStr === "0" || statusStr === "pending";
    } else if (filter === 'failed') {
      matchesFilter = statusStr === "2" || statusStr === "failed";
    }
    
    // Convert id to string before calling toLowerCase
    const idStr = String(item.id || '').toLowerCase();
    const methodStr = String(item.method || '').toLowerCase();
    const txHashStr = String(item.txHash || '').toLowerCase();
    const remarksStr = String(item.remarks || '').toLowerCase();
    const addressStr = String(item.address || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    const matchesSearch = 
      idStr.includes(searchLower) ||
      methodStr.includes(searchLower) ||
      txHashStr.includes(searchLower) ||
      remarksStr.includes(searchLower) ||
      addressStr.includes(searchLower);
    
    return matchesFilter && matchesSearch;
  });

  // Pagination
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const paginatedWithdrawals = filteredWithdrawals.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredWithdrawals.length / entriesPerPage);
  const totalWithdrawn = withdrawals
    .filter(w => String(w.status).toLowerCase() === "1" || String(w.status).toLowerCase() === "success" || String(w.status).toLowerCase() === "completed")
    .reduce((sum, w) => sum + (w.amount || 0), 0);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, entriesPerPage]);

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="withdrawal-history-page">
        <div className="page-header">
          <div>
            <h1>Withdrawal History</h1>
            <p>Track all your withdrawal requests</p>
          </div>
          <div className="total-withdrawn">
            <span>Total Withdrawn</span>
            <strong>${totalWithdrawn.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
          </div>
        </div>

        {/* Filters */}
        <div className="table-filters">
          <div className="search-input-wrapper">
            <i className="fa fa-search"></i>
            <input
              type="text"
              placeholder="Search by ID, Method, TX Hash, Address or Remarks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <select 
              value={entriesPerPage} 
              onChange={(e) => setEntriesPerPage(Number(e.target.value))}
            >
              <option value={10}>10 entries</option>
              <option value={25}>25 entries</option>
              <option value={50}>50 entries</option>
              <option value={100}>100 entries</option>
            </select>
            <button className="refresh-btn" onClick={fetchWithdrawals}>
              <i className="fa fa-refresh"></i> Refresh
            </button>
          </div>
        </div>

        {/* Premium Table Container */}
        <div className="premium-table-container">
          <div className="table-wrapper">
            <table className="premium-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Amount ($)</th>
                  <th>Net ($)</th>
                  <th>Method</th>
                  <th>Wallet Address</th>
                  <th>Date</th>
                  <th>Transaction Hash</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="loading-row">
                    <td colSpan="8">
                      <div className="loader"></div> Loading withdrawal history...
                    </td>
                  </tr>
                ) : error === "No withdrawal records found" ? (
                  <tr className="empty-row">
                    <td colSpan="8">
                      <i className="fa fa-inbox"></i> 
                      <p>No Withdrawal Records Found</p>
                      <small>Your withdrawal history will appear here after you make your first withdrawal request</small>
                    </td>
                  </tr>
                ) : error ? (
                  <tr className="error-row">
                    <td colSpan="8">
                      <i className="fa fa-exclamation-circle"></i> {error}
                    </td>
                  </tr>
                ) : paginatedWithdrawals.length === 0 ? (
                  <tr className="empty-row">
                    <td colSpan="8">
                      <i className="fa fa-filter"></i>
                      <p>No matching records found</p>
                      <small>Try adjusting your search or filter criteria</small>
                    </td>
                  </tr>
                ) : (
                  paginatedWithdrawals.map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td data-label="#">{indexOfFirst + idx + 1}</td>
                      <td data-label="Amount" className="amount">${(item.amount || 0).toFixed(2)}</td>
                      <td data-label="Net">${(item.netamount || 0).toFixed(2)}</td>
                      <td data-label="Method">{item.method}</td>
                      <td data-label="Wallet Address">
                        <code className="address-hash">{item.address}</code>
                      </td>
                      <td data-label="Date">{item.date}</td>
                      <td data-label="Transaction Hash">
                        {item.txHash && item.txHash !== '-' ? (
                          <div className="tx-hash-wrapper">
                            <code className="tx-hash">{item.txHash.slice(0, 16)}...</code>
                            <button 
                              className="copy-hash-btn" 
                              onClick={() => {
                                navigator.clipboard.writeText(item.txHash);
                                toast.success("Hash copied!");
                              }}
                              title="Copy full hash"
                            >
                              <i className="fa fa-copy"></i>
                            </button>
                            <a
                              href={`https://bscscan.com/tx/${item.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="view-tx-link"
                              title="View on BscScan"
                            >
                              <i className="fa fa-external-link"></i>
                            </a>
                          </div>
                        ) : (
                          <span className="pending-text">-</span>
                        )}
                      </td>
                      <td data-label="Status">
                        <StatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && !loading && !error && filteredWithdrawals.length > 0 && (
            <div className="premium-table-footer">
              <div className="table-info">
                Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredWithdrawals.length)} of {filteredWithdrawals.length} entries
              </div>
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                {[...Array(Math.min(totalPages, 10))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 10) {
                    pageNum = i + 1;
                  } else if (currentPage <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 4) {
                    pageNum = totalPages - 9 + i;
                  } else {
                    pageNum = currentPage - 5 + i;
                  }
                  if (pageNum >= 1 && pageNum <= totalPages) {
                    return (
                      <button
                        key={pageNum}
                        className={currentPage === pageNum ? 'active-page' : ''}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return null;
                })}
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .withdrawal-history-page {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          animation: fadeIn 0.5s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
          flex-wrap: wrap;
        }
        
        .page-header h1 {
          font-size: 28px;
          font-weight: 700;
          color: #1a202c;
          margin-bottom: 5px;
        }
        
        .page-header p {
          color: #718096;
          font-size: 14px;
        }
        
        .total-withdrawn {
          background: linear-gradient(135deg, #667eea, #764ba2);
          padding: 15px 25px;
          border-radius: 12px;
          color: white;
          text-align: center;
        }
        
        .total-withdrawn span {
          display: block;
          font-size: 12px;
          opacity: 0.8;
        }
        
        .total-withdrawn strong {
          font-size: 24px;
        }
        
        .table-filters {
          background: white;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 20px;
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .search-input-wrapper {
          flex: 1;
          position: relative;
          min-width: 200px;
        }
        
        .search-input-wrapper i {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #a0aec0;
        }
        
        .search-input-wrapper input {
          width: 100%;
          padding: 10px 10px 10px 35px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.3s;
        }
        
        .search-input-wrapper input:focus {
          outline: none;
          border-color: #667eea;
        }
        
        .filter-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        .filter-group select {
          padding: 10px 15px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
        }
        
        .filter-group select:focus {
          outline: none;
          border-color: #667eea;
        }
        
        .refresh-btn {
          padding: 10px 20px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .refresh-btn:hover {
          transform: translateY(-2px);
        }
        
        .premium-table-container {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .table-wrapper {
          overflow-x: auto;
        }
        
        .premium-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        
        .premium-table th {
          text-align: left;
          padding: 15px;
          background: #f7fafc;
          font-weight: 600;
          color: #4a5568;
          font-size: 13px;
          white-space: nowrap;
        }
        
        .premium-table td {
          padding: 15px;
          border-bottom: 1px solid #edf2f7;
          vertical-align: middle;
        }
        
        .premium-table tr:hover {
          background: #f7fafc;
        }
        
        .amount {
          font-weight: 600;
          color: #2d3748;
        }
        
        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          color: white;
          font-size: 12px;
          font-weight: 600;
        }
        
        .address-hash {
          font-size: 12px;
          font-family: 'Courier New', monospace;
          background: #f7fafc;
          padding: 4px 8px;
          border-radius: 4px;
          color: #4a5568;
        }
        
        .tx-hash-wrapper {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .tx-hash {
          font-size: 12px;
          font-family: 'Courier New', monospace;
          color: #4a5568;
        }
        
        .copy-hash-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #a0aec0;
          padding: 2px 4px;
          transition: color 0.2s;
        }
        
        .copy-hash-btn:hover {
          color: #667eea;
        }
        
        .view-tx-link {
          color: #667eea;
          text-decoration: none;
          padding: 2px 4px;
        }
        
        .view-tx-link:hover {
          color: #764ba2;
        }
        
        .pending-text {
          color: #a0aec0;
        }
        
        .premium-table-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 20px;
          margin-top: 20px;
          border-top: 1px solid #edf2f7;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .table-info {
          color: #718096;
          font-size: 14px;
        }
        
        .pagination {
          display: flex;
          gap: 5px;
        }
        
        .pagination button {
          padding: 8px 14px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .pagination button:hover:not(:disabled) {
          background: #f7fafc;
        }
        
        .pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .pagination button.active-page {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border-color: #667eea;
        }
        
        .loading-row td {
          text-align: center;
          padding: 40px;
          color: #718096;
        }
        
        .loader {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 2px solid #e2e8f0;
          border-top: 2px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 10px;
          vertical-align: middle;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .empty-row td {
          text-align: center;
          padding: 50px 20px;
          color: #718096;
        }
        
        .empty-row i {
          font-size: 48px;
          color: #cbd5e0;
          display: block;
          margin-bottom: 15px;
        }
        
        .empty-row p {
          font-size: 18px;
          margin-bottom: 5px;
        }
        
        .empty-row small {
          color: #a0aec0;
        }
        
        .error-row td {
          text-align: center;
          padding: 30px;
          color: #f56565;
        }
        
        .error-row i {
          margin-right: 10px;
        }
        
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
          }
          
          .total-withdrawn {
            width: 100%;
          }
          
          .table-filters {
            flex-direction: column;
          }
          
          .filter-group {
            flex-direction: column;
          }
          
          .premium-table {
            font-size: 12px;
          }
          
          .premium-table th,
          .premium-table td {
            padding: 10px;
          }
          
          .premium-table-footer {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
    </Layout>
  );
};

export default WithdrawalHistory;